// Utility to merge data type defaults with attribute-specific overrides

interface FilterConfig {
    supported: boolean;
    operations: string[];
}

interface GroupingConfig {
    supported: boolean;
    modes?: string[];
}

interface AggregationConfig {
    supported: boolean;
    operations: string[];
}

interface DataTypeDefault {
    filters?: FilterConfig;
    grouping?: GroupingConfig;
    aggregations?: AggregationConfig;
}

interface AttributeOverride {
    dataType: string;
    reportable: boolean;
    unit?: string;
    note?: string;
    filters?: Partial<FilterConfig>;
    grouping?: Partial<GroupingConfig>;
    aggregations?: Partial<AggregationConfig>;
}

interface ReportableField {
    dataType: string;
    reportable: boolean;
    unit?: string;
    note?: string;
    filters?: FilterConfig;
    grouping?: GroupingConfig;
    aggregations?: AggregationConfig;
}

interface ImprovedSchema {
    dataTypeDefaults: Record<string, DataTypeDefault>;
    attributeOverrides: Record<string, AttributeOverride>;
    globalSettings: {
        dateFormats: string[];
        numberFormats: string[];
        customAggregations: Record<string, string>;
        groupingModes: Record<string, string>;
    };
}

/**
 * Deep merge helper that combines base and override objects
 */
function deepMerge<T extends object>(base: T, override: Partial<T>): T {
    const result = { ...base };

    for (const key in override) {
        if (override.hasOwnProperty(key)) {
            const overrideValue = override[key];
            const baseValue = base[key];

            if (overrideValue !== undefined) {
                if (typeof overrideValue === 'object' && !Array.isArray(overrideValue) &&
                    typeof baseValue === 'object' && !Array.isArray(baseValue)) {
                    // Recursively merge objects
                    result[key] = deepMerge(baseValue as any, overrideValue as any);
                } else {
                    // Override arrays and primitives
                    result[key] = overrideValue as any;
                }
            }
        }
    }

    return result;
}

/**
 * Get the complete configuration for an attribute by merging defaults with overrides
 */
export function getAttributeConfig(
    attributeName: string,
    schema: ImprovedSchema
): ReportableField | null {
    const override = schema.attributeOverrides[attributeName];

    if (!override) {
        return null; // Attribute not found
    }

    if (!override.reportable) {
        // Return minimal config for non-reportable fields
        return {
            dataType: override.dataType,
            reportable: false,
            note: override.note
        };
    }

    // Get the data type defaults
    const dataTypeDefault = schema.dataTypeDefaults[override.dataType];

    if (!dataTypeDefault) {
        // If no defaults exist for this data type, use overrides as-is
        return {
            dataType: override.dataType,
            reportable: override.reportable,
            unit: override.unit,
            note: override.note,
            filters: override.filters as FilterConfig | undefined,
            grouping: override.grouping as GroupingConfig | undefined,
            aggregations: override.aggregations as AggregationConfig | undefined
        };
    }

    // Merge defaults with overrides
    const mergedConfig: ReportableField = {
        dataType: override.dataType,
        reportable: override.reportable,
        unit: override.unit,
        note: override.note
    };

    // Merge filters
    if (dataTypeDefault.filters) {
        mergedConfig.filters = override.filters
            ? deepMerge(dataTypeDefault.filters, override.filters)
            : dataTypeDefault.filters;
    }

    // Merge grouping
    if (dataTypeDefault.grouping) {
        mergedConfig.grouping = override.grouping
            ? deepMerge(dataTypeDefault.grouping, override.grouping)
            : dataTypeDefault.grouping;
    }

    // Merge aggregations
    if (dataTypeDefault.aggregations) {
        mergedConfig.aggregations = override.aggregations
            ? deepMerge(dataTypeDefault.aggregations, override.aggregations)
            : dataTypeDefault.aggregations;
    }

    return mergedConfig;
}

/**
 * Get all reportable attribute configurations
 */
export function getAllReportableAttributes(schema: ImprovedSchema): Record<string, ReportableField> {
    const result: Record<string, ReportableField> = {};

    for (const attributeName in schema.attributeOverrides) {
        const config = getAttributeConfig(attributeName, schema);
        if (config && config.reportable) {
            result[attributeName] = config;
        }
    }

    return result;
}

/**
 * Check if an attribute is reportable
 */
export function isAttributeReportable(attributeName: string, schema: ImprovedSchema): boolean {
    const override = schema.attributeOverrides[attributeName];
    return override?.reportable ?? false;
}

/**
 * Add a new attribute with minimal configuration (it will inherit defaults from data type)
 */
export function addAttributeOverride(
    attributeName: string,
    dataType: string,
    reportable: boolean = true,
    customizations?: Partial<AttributeOverride>
): AttributeOverride {
    return {
        dataType,
        reportable,
        ...customizations
    };
}

export type { ImprovedSchema, ReportableField, AttributeOverride, FilterConfig, GroupingConfig, AggregationConfig };